import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  ShieldCheck, Send, CheckCircle2, Copy, ChevronDown, ChevronUp,
  Eye, Printer, MoreVertical, Clock, FileText, Circle,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import SignaturePad from "@/components/app/SignaturePad";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  draft:          { label: "Draft",             cls: "bg-linen text-ink-mid border border-[#E8EAE9]" },
  sent:           { label: "Sent",              cls: "bg-[#E5E2F0] text-[#4A3D8A]" },
  opened:         { label: "Opened",            cls: "bg-[#E5E9F0] text-[#3B5679]" },
  viewed:         { label: "Viewed",            cls: "bg-[#DCE9E4] text-[#1A5D48]" },
  patient_signed: { label: "Patient signed",    cls: "bg-[#FDF3ED] text-[#B0521F]" },
  complete:       { label: "Fully executed",    cls: "bg-[#E8F0EB] text-[#2D6A4F] font-semibold" },
  expired:        { label: "Expired",           cls: "bg-[#F3E3E1] text-[#8A3D3A]" },
};

const EVENT_LABELS = {
  created:         "Consent created",
  sent:            "Sent to patient",
  shared_whatsapp: "Shared via WhatsApp",
  shared_email:    "Shared via Email",
  opened:          "Patient opened link",
  viewed:          "Patient viewed again",
  patient_signed:  "Patient signed",
  doctor_signed:   "Doctor countersigned",
  pdf_generated:   "PDF generated",
  printed:         "Printed",
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined,
      { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

const fmtShort = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined,
      { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

export default function ConsentCard({ patient }) {
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ template_key: "", tooth: "", estimated_cost: "", extra_notes: "" });
  const [historyOpen, setHistoryOpen] = useState(true);
  const [signOpen, setSignOpen] = useState(false);
  const [signingCid, setSigningCid] = useState(null);
  const [doctorSig, setDoctorSig] = useState(null);
  const [expandedId, setExpandedId] = useState(null);      // Timeline expanded
  const [activities, setActivities] = useState({});         // cid -> activity[]

  const load = useCallback(async () => {
    if (!patient?.id) return;
    const [t, h] = await Promise.all([
      api.get("/consent-templates"),
      api.get(`/patients/${patient.id}/consents`),
    ]);
    setTemplates(t.data);
    setHistory(h.data);
  }, [patient?.id]);
  useEffect(() => { load(); }, [load]);

  // Poll for signature updates every 20s if there's an unsigned consent (to give
  // the clinic a "live" signal that the patient just signed).
  useEffect(() => {
    if (!history.some((c) => c.status === "sent" || c.status === "opened" || c.status === "viewed" || c.status === "patient_signed")) {
      return;
    }
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [history, load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.template_key) return toast.error("Pick a procedure template");
    try {
      const { data } = await api.post(`/patients/${patient.id}/consents`, {
        patient_id: patient.id,
        template_key: form.template_key,
        tooth: form.tooth || null,
        estimated_cost: form.estimated_cost ? Number(form.estimated_cost) : null,
        extra_notes: form.extra_notes || null,
      });
      toast.success("Consent draft created");
      setOpenNew(false);
      setForm({ template_key: "", tooth: "", estimated_cost: "", extra_notes: "" });
      await sendLink(data.id);
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  const sendLink = async (cid) => {
    try {
      const { data } = await api.post(`/consents/${cid}/send-whatsapp`);
      if (data.status === "mocked" || data.provider === "mock") {
        const fullLink = window.location.origin + data.link;
        navigator.clipboard?.writeText(fullLink)?.catch(() => {});
        toast.info("WhatsApp mocked — link copied to clipboard");
      } else {
        toast.success("Consent link sent via WhatsApp");
      }
      load();
    } catch (err) { toast.error(apiError(err, "Failed to send")); }
  };

  const copyLink = (c) => {
    const link = window.location.origin + `/consent/${c.public_token}`;
    navigator.clipboard?.writeText(link)?.catch(() => {});
    toast.success("Link copied");
  };

  const openPdf = (c, variant) => {
    // Signed variant requires patient_signed; backend auto-falls-back so this is safe.
    const url = `${API}/api/consents/${c.id}/pdf?variant=${variant}`;
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("failed"); return r.blob(); })
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(objUrl), 30000);
      })
      .catch(() => toast.error("Could not open PDF"));
  };

  const openDoctorSign = (cid) => {
    setSigningCid(cid);
    setDoctorSig(null);
    setSignOpen(true);
  };

  const submitDoctorSign = async () => {
    if (!doctorSig) return toast.error("Please sign inside the box");
    try {
      await api.post(`/consents/${signingCid}/sign-doctor`, { signature: doctorSig });
      toast.success("Countersigned — consent is now fully executed.");
      setSignOpen(false);
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  const toggleTimeline = async (cid) => {
    if (expandedId === cid) {
      setExpandedId(null);
      return;
    }
    setExpandedId(cid);
    if (!activities[cid]) {
      try {
        const { data } = await api.get(`/consents/${cid}/activity`);
        setActivities((s) => ({ ...s, [cid]: data.activity || [] }));
      } catch {
        setActivities((s) => ({ ...s, [cid]: [] }));
      }
    }
  };

  return (
    <div className="card-widget" data-testid="consent-card">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-ink-mid" />
          <div className="overline text-ink-soft">Digital consent</div>
        </div>
        <div className="flex items-center gap-2">
          {history.length > 0 && (
            <button data-testid="consent-history-toggle" onClick={() => setHistoryOpen(!historyOpen)}
              className="text-xs text-ink-mid hover:text-ink inline-flex items-center gap-1">
              {history.length} on record {historyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <button data-testid="consent-new-btn" className="btn-primary text-xs inline-flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> New consent
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-display text-2xl">New consent form</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3" data-testid="consent-new-form">
                <select required data-testid="consent-template" value={form.template_key}
                  onChange={(e) => setForm({ ...form, template_key: e.target.value })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
                  <option value="">Pick a procedure…</option>
                  {templates.map((t) => <option key={t.key} value={t.key}>{t.title}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="consent-tooth" placeholder="Tooth # (optional)" value={form.tooth}
                    onChange={(e) => setForm({ ...form, tooth: e.target.value })}
                    className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft text-sm">₹</span>
                    <input data-testid="consent-cost" type="number" min={0} placeholder="Est. cost (optional)"
                      value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })}
                      className="w-full rounded-xl border border-[#E8EAE9] pl-7 pr-4 py-2.5 text-sm" />
                  </div>
                </div>
                <textarea data-testid="consent-notes" placeholder="Extra notes for the patient (optional)"
                  value={form.extra_notes} onChange={(e) => setForm({ ...form, extra_notes: e.target.value })}
                  rows={2}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm resize-none" />
                <button data-testid="consent-create-submit" className="w-full btn-primary text-sm inline-flex items-center justify-center gap-2">
                  <Send className="w-3.5 h-3.5" /> Create &amp; send WhatsApp link
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <p className="text-xs text-ink-mid mb-4">
        Legally-defensible record of informed consent. Every action is timestamped;
        once both parties sign, the document is locked as a permanent legal record.
      </p>

      {historyOpen && (
        <div className="space-y-2" data-testid="consent-history">
          {history.length === 0 ? (
            <div className="text-sm text-ink-soft text-center py-6">No consents on record yet.</div>
          ) : history.map((c) => {
            const style = STATUS[c.status] || STATUS.draft;
            const isSigned = !!c.patient_signed_at;
            const isComplete = c.status === "complete";
            const isLocked = isComplete;
            const isTimelineOpen = expandedId === c.id;
            return (
              <div key={c.id} data-testid={`consent-row-${c.id}`}
                className={`border rounded-2xl p-3 ${isComplete ? "border-[#C4D8CC] bg-[#F4F9F5]" : "border-[#E8EAE9]"}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-ink truncate">{c.title}</div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest ${style.cls}`}>
                        {style.label}
                      </span>
                      {isLocked && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-pine text-linen inline-flex items-center gap-0.5" title="Locked — immutable record">
                          🔒 locked
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-ink-soft mt-1">
                      Created {fmtDateTime(c.created_at)}
                      {c.tooth && ` · Tooth ${c.tooth}`}
                      {c.estimated_cost != null && ` · ₹${Number(c.estimated_cost).toLocaleString("en-IN")}`}
                    </div>

                    {/* Inline status ladder — compact */}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-2 text-[10.5px]">
                      {c.sent_channels?.[0] && (
                        <StatusLine label="Sent" at={c.sent_channels[0].at} done />
                      )}
                      <StatusLine label="Opened" at={c.opened_at} done={!!c.opened_at} />
                      <StatusLine label="Patient signed" at={c.patient_signed_at} done={!!c.patient_signed_at} />
                      <StatusLine label="Doctor signed" at={c.doctor_signed_at} done={!!c.doctor_signed_at} />
                    </div>

                    {/* Expanded timeline */}
                    {isTimelineOpen && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-[#E8EAE9]" data-testid={`consent-timeline-${c.id}`}>
                        <div className="text-[10px] uppercase tracking-wider text-ink-soft mb-2">Activity timeline</div>
                        {(activities[c.id] || []).length === 0 ? (
                          <div className="text-xs text-ink-soft">Loading…</div>
                        ) : (
                          <ul className="space-y-1.5">
                            {(activities[c.id] || []).map((e, i) => (
                              <li key={i} className="flex items-start gap-2 text-xs">
                                <CheckCircle2 className="w-3.5 h-3.5 text-[#2D6A4F] mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <span className="text-ink">{EVENT_LABELS[e.event] || e.event}</span>
                                  {e.actor && e.actor !== "system" && <span className="text-ink-soft"> · {e.actor}</span>}
                                </div>
                                <span className="text-ink-soft flex-shrink-0">{fmtShort(e.at)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <ActionBtn testid={`consent-view-${c.id}`} onClick={() => openPdf(c, isSigned ? "signed" : "original")}
                               icon={Eye} label={isSigned ? "View signed" : "View"} />
                    <ActionBtn testid={`consent-print-${c.id}`} onClick={() => openPdf(c, isSigned ? "signed" : "original")}
                               icon={Printer} label="Print" />
                    {!isLocked && (
                      <ActionBtn testid={`consent-send-${c.id}`} onClick={() => sendLink(c.id)}
                                 icon={Send} label="Send" primary />
                    )}
                    <ActionBtn testid={`consent-copy-${c.id}`} onClick={() => copyLink(c)}
                               icon={Copy} label="Copy" />
                    <Popover>
                      <PopoverTrigger asChild>
                        <button data-testid={`consent-more-${c.id}`}
                                className="p-1.5 rounded-full text-ink-mid hover:bg-linen"
                                title="More">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-56 p-1">
                        <MenuItem icon={Clock} label={isTimelineOpen ? "Hide timeline" : "View timeline"}
                                  testid={`consent-timeline-toggle-${c.id}`}
                                  onClick={() => toggleTimeline(c.id)} />
                        <MenuItem icon={FileText} label="Open original PDF"
                                  testid={`consent-view-original-${c.id}`}
                                  onClick={() => openPdf(c, "original")} />
                        {isSigned && (
                          <MenuItem icon={FileText} label="Open signed PDF"
                                    testid={`consent-view-signed-${c.id}`}
                                    onClick={() => openPdf(c, "signed")} />
                        )}
                        {c.status === "patient_signed" && (
                          <MenuItem icon={CheckCircle2} label="Doctor countersign"
                                    testid={`consent-doctor-sign-${c.id}`}
                                    onClick={() => openDoctorSign(c.id)} />
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {c.status === "patient_signed" && (
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-[#B0521F]">
                    <Clock className="w-3.5 h-3.5" /> Waiting for doctor countersignature
                    <button data-testid={`consent-doctor-sign-inline-${c.id}`}
                      onClick={() => openDoctorSign(c.id)}
                      className="ml-auto text-[11px] px-2.5 py-1 rounded-full bg-pine text-linen hover:bg-pine-mid">
                      Countersign now
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Doctor countersign modal */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">Doctor countersignature</DialogTitle></DialogHeader>
          <p className="text-xs text-ink-mid -mt-2 mb-2">Sign below to complete the consent record. Timestamp and your account will be logged.</p>
          <SignaturePad testid="doctor-sig" onChange={setDoctorSig} height={160} />
          <button data-testid="doctor-sign-submit" onClick={submitDoctorSign} className="w-full btn-primary text-sm mt-3">Complete consent</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusLine({ label, at, done }) {
  return (
    <span className={`inline-flex items-center gap-1 ${done ? "text-ink-mid" : "text-ink-disabled"}`}>
      {done ? <CheckCircle2 className="w-3 h-3 text-[#2D6A4F]" /> : <Circle className="w-3 h-3" />}
      <span>{label}</span>
      {done && at && <span className="text-ink-soft">· {fmtShort(at)}</span>}
    </span>
  );
}

function ActionBtn({ icon: Icon, label, onClick, testid, primary }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      title={label}
      className={`text-[11px] px-2.5 py-1 rounded-full inline-flex items-center gap-1 whitespace-nowrap ${
        primary ? "bg-pine text-linen hover:bg-pine-mid"
                : "border border-pine/20 text-ink hover:bg-linen"
      }`}>
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="w-full text-left px-3 py-2 rounded-lg hover:bg-linen text-sm text-ink inline-flex items-center gap-2">
      <Icon className="w-4 h-4 text-ink-mid" /> {label}
    </button>
  );
}
